// src/components/settings/AttendancePolicyPage.js
import React, { useState, useEffect, useCallback } from "react";
import Icon from "../icons/Icon";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import {
  getAttendancePolicy,
  saveAttendancePolicy,
  DEFAULT_ATTENDANCE_POLICY,
} from "../../services/attendancePolicyService";
import "../../styles/components/settings-pages.css";
import "../../styles/components/attendance-policy.css";

// Night differential time options
const NIGHT_START_OPTIONS = [
  { value: 20, label: "8:00 PM" },
  { value: 21, label: "9:00 PM" },
  { value: 22, label: "10:00 PM" },
  { value: 23, label: "11:00 PM" },
];

const NIGHT_END_OPTIONS = [
  { value: 4, label: "4:00 AM" },
  { value: 5, label: "5:00 AM" },
  { value: 6, label: "6:00 AM" },
  { value: 7, label: "7:00 AM" },
];

// OT Increment options
const OT_INCREMENT_OPTIONS = [
  { value: 0, label: "Off" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
];

// Leeway field config
const LEEWAY_MIN = 0;
const LEEWAY_MAX = 240;

const AttendancePolicyPage = () => {
  const { company, hasPermission } = useAuth();
  const canEdit = hasPermission("set:att-policy:edit");
  const [policy, setPolicy] = useState(DEFAULT_ATTENDANCE_POLICY);
  const [initialPolicy, setInitialPolicy] = useState(DEFAULT_ATTENDANCE_POLICY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [leewayErrors, setLeewayErrors] = useState({});

  // Fetch policy from Firestore
  useEffect(() => {
    const fetchPolicy = async () => {
      if (!company?.id) return;

      try {
        setLoading(true);
        const data = await getAttendancePolicy(company.id);
        // hoursPerDay's home is attendancePolicy (C4). Pre-backfill companies
        // still carry it on payrollRates — prefill from there so the first
        // save self-migrates it; default 8 when neither doc has it.
        if (typeof data.hoursPerDay !== "number") {
          const rates = await getDoc(
            doc(db, "companies", company.id, "settings", "payrollRates")
          );
          const legacy = rates.exists() ? rates.data().hoursPerDay : undefined;
          data.hoursPerDay = typeof legacy === "number" ? legacy : 8;
        }
        setPolicy(data);
        setInitialPolicy(data);
      } catch (err) {
        console.error("Error fetching attendance policy:", err);
        setError("Failed to load attendance policy. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchPolicy();
  }, [company?.id]);

  // Validate a single leeway field
  const validateLeeway = useCallback((key, value) => {
    const num = parseInt(value, 10);
    if (key === "otAutoApprovalThreshold") {
      if (isNaN(num) || num < 0 || num > 8) {
        return "Must be between 0 and 8 hours";
      }
      return null;
    }
    if (key === "hoursPerDay") {
      if (isNaN(num) || num < 1 || num > 24) {
        return "Must be between 1 and 24 hours";
      }
      return null;
    }
    if (isNaN(num) || num < LEEWAY_MIN || num > LEEWAY_MAX) {
      return "Must be between 0 and 240 minutes";
    }
    return null;
  }, []);

  // Check if there are unsaved changes
  const hasChanges = useCallback(() => {
    return Object.keys(policy).some(
      (key) => policy[key] !== initialPolicy[key]
    );
  }, [policy, initialPolicy]);

  // Check if all validations pass
  const isValid = useCallback(() => {
    return Object.keys(leewayErrors).length === 0;
  }, [leewayErrors]);

  // Handle leeway field change
  const handleLeewayChange = (key, value) => {
    const numValue = value === "" ? "" : parseInt(value, 10);
    const newPolicy = { ...policy, [key]: numValue };
    setPolicy(newPolicy);

    // Validate
    const err = validateLeeway(key, value);
    const newErrors = { ...leewayErrors };
    if (err) {
      newErrors[key] = err;
    } else {
      delete newErrors[key];
    }
    setLeewayErrors(newErrors);
  };

  // Handle integer setting change (nightDiff, hours, minutes, break)
  const handleSettingChange = (key, value) => {
    const numValue = parseInt(value, 10) || 0;
    setPolicy({ ...policy, [key]: numValue });
  };

  // Handle cancel
  const handleCancel = () => {
    setPolicy(initialPolicy);
    setLeewayErrors({});
  };

  // Handle save
  const handleSave = async () => {
    if (!company?.id || !isValid()) return;

    try {
      setSaving(true);
      // Only save fields managed by this page — exclude legacy fields
      const { leewayEarlyTimeIn, leewayLateGracePeriod, leewayOtStart, leewayUndertimeGracePeriod, otIncrement, nightDiffStart, nightDiffEnd, otAutoApprovalThreshold, otApprovalType, hoursPerDay } = policy;
      await saveAttendancePolicy(company.id, { leewayEarlyTimeIn, leewayLateGracePeriod, leewayOtStart, leewayUndertimeGracePeriod, otIncrement, nightDiffStart, nightDiffEnd, otAutoApprovalThreshold, otApprovalType, hoursPerDay });
      setInitialPolicy(policy);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      console.error("Error saving attendance policy:", err);
      setError("Failed to save attendance policy. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h1 className="settings-page-title">Attendance Policy</h1>
        <p className="settings-page-subtitle">
          Configure how clock-in and clock-out times are interpreted during
          payroll computation
        </p>
        <div className="settings-card">
          <div className="settings-card-body" style={{ textAlign: "center", padding: "60px 24px", color: "var(--ui-text-muted)" }}>
            Loading attendance policy...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1 className="settings-page-title">Attendance Policy</h1>
        <div className="settings-card">
          <div className="settings-card-body" style={{ textAlign: "center", padding: "60px 24px", color: "var(--ui-danger)" }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="settings-page-title">Attendance Policy</h1>
      <p className="settings-page-subtitle">
        Configure how clock-in and clock-out times are interpreted during
        payroll computation
      </p>

      {/* Info Banner */}
      <div className="ap-info-banner">
        <Icon name="alert-circle" className="ap-info-banner-icon" />
        <div className="ap-info-banner-text">
          Leeway settings affect payroll computation only — raw clock-in and
          clock-out times in time records are never modified. Scheduled start and
          end times are taken from each employee's assigned work schedule.
        </div>
      </div>

      {/* Clock-In Rules Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon" style={{ background: "var(--ui-primary-tint)", color: "var(--ui-primary)" }}>
              <Icon name="upload-cloud" size={20} />
            </div>
            <div>
              <h2 className="settings-card-title">Clock-In Rules</h2>
              <p className="settings-card-description">
                How early arrivals and late arrivals are handled
              </p>
            </div>
          </div>
        </div>
        <div className="settings-card-body">
          <div className="ap-leeway-grid">
            {/* leewayEarlyTimeIn */}
            <div
              className={`ap-leeway-item${leewayErrors.leewayEarlyTimeIn ? " has-error" : ""}`}
            >
              <div className="ap-leeway-item-label">Early Clock-In Leeway</div>
              <div className="ap-leeway-input-row">
                <input
                  className="ap-leeway-input"
                  type="number"
                  value={policy.leewayEarlyTimeIn}
                  onChange={(e) =>
                    handleLeewayChange("leewayEarlyTimeIn", e.target.value)
                  }
                  min={LEEWAY_MIN}
                  max={LEEWAY_MAX}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
                <span className="ap-leeway-unit">minutes</span>
              </div>
              <div className="ap-leeway-item-desc">
                If an employee clocks in within this window before their
                scheduled start, their effective start time is set to the
                scheduled start.
              </div>
              <div className="ap-leeway-item-example">
                e.g. 15 min leeway, 6:00am shift — clock in at 5:50am →{" "}
                <span>effective start = 6:00am</span>
              </div>
              <div className="ap-leeway-error-msg">
                Must be between 0 and 240 minutes
              </div>
            </div>

            {/* leewayLateGracePeriod */}
            <div
              className={`ap-leeway-item${leewayErrors.leewayLateGracePeriod ? " has-error" : ""}`}
            >
              <div className="ap-leeway-item-label">Late Grace Period</div>
              <div className="ap-leeway-input-row">
                <input
                  className="ap-leeway-input"
                  type="number"
                  value={policy.leewayLateGracePeriod}
                  onChange={(e) =>
                    handleLeewayChange("leewayLateGracePeriod", e.target.value)
                  }
                  min={LEEWAY_MIN}
                  max={LEEWAY_MAX}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
                <span className="ap-leeway-unit">minutes</span>
              </div>
              <div className="ap-leeway-item-desc">
                Arrivals within this window after scheduled start are not flagged
                as late and incur no late deduction.
              </div>
              <div className="ap-leeway-item-example">
                e.g. 15 min grace, 6:00am shift — clock in at 6:10am →{" "}
                <span>not late</span>
              </div>
              <div className="ap-leeway-error-msg">
                Must be between 0 and 240 minutes
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clock-Out Rules Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon" style={{ background: "var(--ui-warning-tint)", color: "var(--ui-accent)" }}>
              <Icon name="download-cloud" size={20} />
            </div>
            <div>
              <h2 className="settings-card-title">Clock-Out Rules</h2>
              <p className="settings-card-description">
                How overtime and early departures are handled
              </p>
            </div>
          </div>
        </div>
        <div className="settings-card-body">
          <div className="ap-leeway-grid">
            {/* leewayOtStart */}
            <div
              className={`ap-leeway-item${leewayErrors.leewayOtStart ? " has-error" : ""}`}
            >
              <div className="ap-leeway-item-label">OT Start Leeway</div>
              <div className="ap-leeway-input-row">
                <input
                  className="ap-leeway-input"
                  type="number"
                  value={policy.leewayOtStart}
                  onChange={(e) =>
                    handleLeewayChange("leewayOtStart", e.target.value)
                  }
                  min={LEEWAY_MIN}
                  max={LEEWAY_MAX}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
                <span className="ap-leeway-unit">minutes</span>
              </div>
              <div className="ap-leeway-item-desc">
                Overtime is only counted if the employee stays beyond this window
                after their scheduled end. Minor stays are ignored.
              </div>
              <div className="ap-leeway-item-example">
                e.g. 15 min leeway, 6:00pm shift — clock out at 6:10pm →{" "}
                <span>no OT recorded</span>
              </div>
              <div className="ap-leeway-error-msg">
                Must be between 0 and 240 minutes
              </div>
            </div>

            {/* hoursPerDay — moved here from Pay Rates (PRD_MonthlySalaryType C4) */}
            <div
              className={`ap-leeway-item${leewayErrors.hoursPerDay ? " has-error" : ""}`}
            >
              <div className="ap-leeway-item-label">Hours Per Day</div>
              <div className="ap-leeway-input-row">
                <input
                  className="ap-leeway-input"
                  type="number"
                  value={policy.hoursPerDay ?? ""}
                  onChange={(e) =>
                    handleLeewayChange("hoursPerDay", e.target.value)
                  }
                  min={1}
                  max={24}
                  disabled={!canEdit}
                />
              </div>
              <div className="ap-leeway-item-desc">
                Standard workday length. Overtime and undertime measure against
                it, and payroll derives the hourly rate from it.
              </div>
              <div className="ap-leeway-item-example">
                e.g. 8 hours — a 10-hour day records <span>2h overtime</span>
              </div>
              <div className="ap-leeway-error-msg">
                Must be between 1 and 24 hours
              </div>
            </div>

            {/* otIncrement */}
            <div className="ap-leeway-item">
              <div className="ap-leeway-item-label">OT Blocks</div>
              <div className="ap-leeway-input-row">
                <select
                  className="ap-leeway-input"
                  value={policy.otIncrement}
                  onChange={(e) =>
                    handleSettingChange("otIncrement", e.target.value)
                  }
                  disabled={!canEdit}
                >
                  {OT_INCREMENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ap-leeway-item-desc">
                Overtime is paid in blocks of this size. Minutes beyond the last
                complete block are not paid.
              </div>
              <div className="ap-leeway-item-example">
                e.g. 15 min increment — employee works 38min OT →{" "}
                <span>paid 30min OT</span>
              </div>
            </div>

            {/* leewayUndertimeGracePeriod */}
            <div
              className={`ap-leeway-item${leewayErrors.leewayUndertimeGracePeriod ? " has-error" : ""}`}
            >
              <div className="ap-leeway-item-label">Undertime Leeway</div>
              <div className="ap-leeway-input-row">
                <input
                  className="ap-leeway-input"
                  type="number"
                  value={policy.leewayUndertimeGracePeriod}
                  onChange={(e) =>
                    handleLeewayChange(
                      "leewayUndertimeGracePeriod",
                      e.target.value
                    )
                  }
                  min={LEEWAY_MIN}
                  max={LEEWAY_MAX}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
                <span className="ap-leeway-unit">minutes</span>
              </div>
              <div className="ap-leeway-item-desc">
                Departures within this window before scheduled end are not
                counted as undertime and incur no deduction.
              </div>
              <div className="ap-leeway-item-example">
                e.g. 15 min grace, 6:00pm shift — clock out at 5:50pm →{" "}
                <span>no undertime</span>
              </div>
              <div className="ap-leeway-error-msg">
                Must be between 0 and 240 minutes
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Night Differential Window Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon" style={{ background: "var(--ui-accent-tint)", color: "var(--ui-warning-text)" }}>
              <Icon name="moon" size={20} />
            </div>
            <div>
              <h2 className="settings-card-title">Night Differential Hours</h2>
              <p className="settings-card-description">
                Time window when the night differential rate applies
              </p>
            </div>
          </div>
        </div>
        <div className="settings-card-body">
          <div className="ap-form-grid">
            <div className="settings-input-group">
              <label className="settings-input-label">
                Night Diff Start Time
              </label>
              <select
                className="settings-input-field"
                value={policy.nightDiffStart}
                onChange={(e) =>
                  handleSettingChange("nightDiffStart", e.target.value)
                }
                disabled={!canEdit}
              >
                {NIGHT_START_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="settings-input-hint">
                When night differential rate begins
              </div>
            </div>

            <div className="settings-input-group">
              <label className="settings-input-label">
                Night Diff End Time
              </label>
              <select
                className="settings-input-field"
                value={policy.nightDiffEnd}
                onChange={(e) =>
                  handleSettingChange("nightDiffEnd", e.target.value)
                }
                disabled={!canEdit}
              >
                {NIGHT_END_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="settings-input-hint">
                When night differential rate ends
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OT Approval Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-header-left">
            <div className="settings-card-icon" style={{ background: "var(--ui-success-tint)", color: "var(--ui-success)" }}>
              <Icon name="shield-check" size={20} />
            </div>
            <div>
              <h2 className="settings-card-title">OT Approval</h2>
              <p className="settings-card-description">
                Set who approves overtime and when it's auto-approved.
              </p>
            </div>
          </div>
        </div>
        <div className="settings-card-body">
          {/* Approval policy — self-approval vs maker-checker */}
          <div className="settings-info-box">
            <span className="settings-info-box-icon"><Icon name="info" size={16} /></span>
            <span className="settings-info-box-text">
              Overtime above the threshold needs approval. Choose whether the
              person who entered it may approve their own overtime.
            </span>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 16, marginBottom: 24 }}>
            {[
              {
                value: "self-approval",
                label: "Self-approval",
                help: "The submitter can approve or reject their own overtime entries.",
              },
              {
                value: "maker-checker",
                label: "Maker-checker",
                help: "The submitter cannot approve or reject their own overtime entries — a different person must.",
              },
            ].map((opt) => {
              const active = (policy.otApprovalType || "self-approval") === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setPolicy({ ...policy, otApprovalType: opt.value })}
                  style={{
                    flex: 1,
                    textAlign: "left",
                    padding: "12px 16px",
                    borderRadius: 8,
                    border: `2px solid ${active ? "var(--ui-primary)" : "var(--ui-border)"}`,
                    background: active ? "var(--ui-primary-tint)" : "var(--ui-surface)",
                    cursor: canEdit ? "pointer" : "not-allowed",
                    color: "var(--ui-text)",
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {opt.label}
                    {opt.value === "self-approval" && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: "var(--ui-text-muted)", fontWeight: 400 }}>
                        Default
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--ui-text-secondary)", lineHeight: 1.4 }}>
                    {opt.help}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="ap-leeway-grid">
            <div
              className={`ap-leeway-item${leewayErrors.otAutoApprovalThreshold ? " has-error" : ""}`}
            >
              <div className="ap-leeway-item-label">Auto-Approval Threshold</div>
              <div className="ap-leeway-input-row">
                <input
                  className="ap-leeway-input"
                  type="number"
                  value={policy.otAutoApprovalThreshold}
                  onChange={(e) =>
                    handleLeewayChange("otAutoApprovalThreshold", e.target.value)
                  }
                  min={0}
                  max={8}
                  disabled={!canEdit}
                  readOnly={!canEdit}
                />
                <span className="ap-leeway-unit">hours</span>
              </div>
              <div className="ap-leeway-item-desc">
                DTRs with OT hours up to this threshold are automatically
                approved. Set to 0 to require manual approval for all overtime.
              </div>
              <div className="ap-leeway-item-example">
                e.g. threshold = 2 — employee with 2hr OT →{" "}
                <span>auto-approved</span>, 2.5hr OT →{" "}
                <span>requires approval</span>
              </div>
              <div className="ap-leeway-error-msg">
                Must be between 0 and 8 hours
              </div>
            </div>
          </div>
          {policy.otAutoApprovalThreshold === 0 && (
            <div className="ap-info-banner" style={{ marginTop: 16, marginBottom: 0 }}>
              <Icon name="alert-circle" className="ap-info-banner-icon" />
              <div className="ap-info-banner-text">
                All DTRs with overtime hours will require manual approval.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Save Bar */}
      <div
        className={`settings-floating-save-bar${
          canEdit && (hasChanges() || saveSuccess) ? " visible" : ""
        }${saveSuccess ? " saved" : ""}`}
      >
        <div className="settings-save-bar-content">
          <div
            className={`settings-save-bar-left${
              saveSuccess ? " success" : ""
            }`}
          >
            <span className="settings-save-bar-dot"></span>
            <span className="settings-save-bar-check">
              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </span>
            <span className="settings-save-bar-text">
              {saveSuccess
                ? "Changes saved. Applies to new time records only."
                : !isValid()
                ? "Fix validation errors to save"
                : "Unsaved changes"}
            </span>
          </div>
          {!saveSuccess && (
            <div className="settings-save-bar-actions">
              <button
                className="settings-btn settings-btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Discard
              </button>
              <button
                className="settings-btn settings-btn-primary"
                onClick={handleSave}
                disabled={!hasChanges() || !isValid() || saving}
              >
                {saving ? (
                  <>
                    <span className="settings-btn-spinner"></span>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendancePolicyPage;
